import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  BookOpen, AlertTriangle, CheckCircle, XCircle, Star, 
  Target, Lightbulb, Info
} from 'lucide-react';
import { getScoringConfig } from '@/lib/api';

export default function UserScoringGuide() {
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await getScoringConfig();
      setConfig(res.data);
    } catch (error) {
      console.error('Failed to load scoring config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const thresholds = config?.classification_thresholds || {
    best_min: 8.5, good_min: 7.0, improvement_min: 5.5, high_alert_max: 5.4
  };

  const CLASSIFICATION_DISPLAY = [
    { classification: 'Best', range: `${thresholds.best_min} - 10`, color: 'bg-emerald-500', emoji: '🌟' },
    { classification: 'Good', range: `${thresholds.good_min} - ${(thresholds.best_min - 0.1).toFixed(1)}`, color: 'bg-blue-500', emoji: '👍' },
    { classification: 'Improvement', range: `${thresholds.improvement_min} - ${(thresholds.good_min - 0.1).toFixed(1)}`, color: 'bg-amber-500', emoji: '📈' },
    { classification: 'High Alert', range: `0 - ${thresholds.high_alert_max}`, color: 'bg-red-500', emoji: '⚠️' }
  ];

  return (
    <div className="space-y-8" data-testid="user-scoring-guide">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-card/20 rounded-lg">
            <BookOpen className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">How Your Calls Are Scored</h1>
            <p className="text-violet-100 mt-1">
              Understand the scoring system to improve your performance
            </p>
          </div>
        </div>
      </div>

      {/* Quick Classification Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Score Classifications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {CLASSIFICATION_DISPLAY.map((cls) => (
              <div key={cls.classification} className="text-center p-4 rounded-lg border">
                <div className="text-3xl mb-2">{cls.emoji}</div>
                <div className={`inline-block px-3 py-1 rounded-full text-white text-sm font-medium ${cls.color}`}>
                  {cls.classification}
                </div>
                <p className="text-lg font-bold mt-2">{cls.range}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Scoring Parameters with Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5" />
            The {config?.parameters?.length || 10} Scoring Dimensions
          </CardTitle>
          <CardDescription>
            Each dimension is scored 1-10. Your average determines your classification.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {config?.parameters?.map((param, idx) => (
            <div key={param.id} className="flex items-start gap-4 p-4 bg-muted/50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-violet-600 font-bold flex-shrink-0">
                {idx + 1}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-semibold">{param.name}</h4>
                  <Badge variant="secondary">{param.weight}%</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{param.description}</p>
                {param.tips && (
                  <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded">
                    <Lightbulb className="h-4 w-4" />
                    <span>{param.tips}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Fatal Parameters - Things to Avoid */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Critical Mistakes to Avoid
          </CardTitle>
          <CardDescription>
            These will automatically flag your call as High Alert
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {config?.fatal_parameters?.map((param) => (
            <div key={param.id} className="border border-red-100 rounded-lg overflow-hidden">
              <div className="p-4 bg-red-50 flex items-start gap-3">
                <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-red-800">{param.violation}</h4>
                  <p className="text-sm text-red-600 mt-1">{param.description}</p>
                </div>
              </div>
              {param.fix && (
                <div className="p-4 bg-card flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">How to fix:</p>
                    <p className="text-sm text-emerald-600">{param.fix}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Pre-Screening Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            Pre-Screening Checklist
          </CardTitle>
          <CardDescription>
            Make sure to cover these items during your screening calls
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {config?.pre_screening_checklist?.map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <div className="w-6 h-6 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center text-sm font-medium">
                  {i + 1}
                </div>
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <Lightbulb className="h-5 w-5" />
            Quick Tips for Higher Scores
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {config?.tips?.map((tip, i) => (
              <div key={i} className="flex items-start gap-2 text-amber-900">
                <span className="text-lg">{i + 1}️⃣</span>
                <span className="text-sm">{tip}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
